if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * Preserves Enterprise theme behavior and adds optional War Buffalo
     * real Shopify add-on products after the primary customized item.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');
      if (!this.submitBtn) return;
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      let primaryData = null;

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        primaryData = await response.json();

        let error = typeof primaryData.description === 'string'
          ? primaryData.description
          : primaryData.message;

        if (primaryData.errors && typeof primaryData.errors === 'object') {
          error = Object.entries(primaryData.errors).map((item) => item[1].join(', '));
        }

        if (primaryData.status) this.setErrorMsgState(error);
        if (!response.ok) throw new Error(response.status);

        // Optional WB add-on products. These only exist when the merchant
        // has selected real Shopify fee products in the customizer settings.
        const addonData = await this.addWarBuffaloAddons(primaryData, sections);
        const renderData = addonData || primaryData;

        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          ProductForm.updateCartIcon(renderData);

          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                renderData,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === primaryData.variant_id
        )[0];

        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: primaryData.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === primaryData.quantity)
                ? itemInOldCart.quantity : primaryData.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: primaryData.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Add real Shopify fee products configured by the War Buffalo customizer.
     * Main product is already in cart so artwork file upload can travel through
     * the native multipart FormData request. Add-ons are added in one supported
     * multi-item Cart Ajax request and carry the same private WB Build ID.
     * @param {object} primaryData - Primary /cart/add response.
     * @param {string} sections - Theme section IDs for bundled rendering.
     * @returns {?object} Add-on response with freshly rendered cart sections.
     */
    async addWarBuffaloAddons(primaryData, sections) {
      // Customizer controls live outside the product <form> and are associated
      // through their `form` attribute. `form.querySelector()` cannot see those
      // controls, but `form.elements` includes them as required by HTML.
      const addonsInput = this.form.elements.namedItem('properties[_WB Addons]');
      if (!addonsInput?.value) return null;

      let addons = [];
      try {
        addons = JSON.parse(addonsInput.value);
      } catch (error) {
        return null;
      }

      if (!Array.isArray(addons) || addons.length === 0) return null;

      const quantityInput = this.form.elements.namedItem('quantity');
      const primaryQuantity = Math.max(1, Number(quantityInput?.value || 1));
      const buildId = this.form.elements.namedItem('properties[_WB Build ID]')?.value || '';
      const parentKey = primaryData?.key || '';
      const productTitle = this.form.closest('.js-product')?.querySelector('.product-title')?.textContent?.trim() || '';

      const items = addons
        .filter((addon) => Number(addon.id) > 0)
        .map((addon) => ({
          id: Number(addon.id),
          quantity: addon.perItem === false ? 1 : primaryQuantity,
          properties: {
            '_WB Build ID': buildId,
            '_WB Parent Key': parentKey,
            '_WB Add-on': 'true',
            '_WB Per Item': addon.perItem === false ? 'false' : 'true',
            '_WB Add-on Label': String(addon.label || 'Customization'),
            '_WB For Product': productTitle
          }
        }));

      if (!items.length) return null;

      const addonResponse = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname
        })
      });

      const addonData = await addonResponse.json();

      if (!addonResponse.ok) {
        // The main customized line uses a unique WB Build ID, so roll it back
        // if an associated fee product cannot be added. This prevents the
        // customer from checking out at a lower price than the displayed build.
        await this.rollbackPrimaryItem(primaryData);

        const description = addonData?.description || addonData?.message || 'Customization fee could not be added.';
        this.setErrorMsgState(description);
        throw new Error(description);
      }

      return addonData;
    }

    /**
     * Removes the primary customized line if its required fee add-on fails.
     * @param {object} primaryData - Primary cart/add response.
     */
    async rollbackPrimaryItem(primaryData) {
      if (!primaryData?.key) return;
      const root = window.Shopify?.routes?.root || '/';
      try {
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: primaryData.key,
            quantity: 0
          })
        });
      } catch (error) {
        console.warn('War Buffalo rollback failed', error); // eslint-disable-line
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response?.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
